import type { IPublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import type { ReactNode } from "react";
import { Provider } from "react-redux";
import { BrowserRouter, Route, Routes } from "react-router";

import type { configureStore } from "@/store/configure-store";
import { Authentication } from "@/utils";

import App from "../features/App";

interface RootProps {
    store: ReturnType<typeof configureStore>;
    msal: IPublicClientApplication | null;
}

/**
 * Root application component wrapping the app with MSAL, auth, Redux, and router providers.
 *
 * @param {RootProps} props - The component props.
 * @returns {ReactNode} The rendered root component.
 */
const Root = ({ store, msal }: RootProps): ReactNode => {
    const authContent = (
        <Provider store={store}>
            <BrowserRouter>
                <Routes>
                    <Route path="/" element={<App />} />
                </Routes>
            </BrowserRouter>
        </Provider>
    );

    if (!msal) {
        return (
            <Authentication.LocalAuthProvider>
                {authContent}
            </Authentication.LocalAuthProvider>
        );
    }

    return (
        <MsalProvider instance={msal}>
            <Authentication.AuthProvider>
                {authContent}
            </Authentication.AuthProvider>
        </MsalProvider>
    );
};

export default Root;
